import type { AxiosResponse } from 'axios'

import { AdminApiError, adminClient } from '@/api/clients/admin-client'
import type { ApiResponse } from '@/shared/types/http'
import type { AdminPageData, AdminPageQuery, AdminQueryFilters } from '@/shared/types/admin'

import type {
  AdminBaseCrudApi,
  AdminBaseId,
  AdminBaseRecord,
  AdminBlobDownload
} from './types'

const BASE_RESOURCE_PATH = /^\/base\/[a-z][a-z\d]*$/
const TOKEN_QUERY_KEY = /^token$/i

const requireResourcePath = (path: string): string => {
  const normalized = path.trim().replace(/\/$/, '')
  if (!BASE_RESOURCE_PATH.test(normalized)) {
    throw new Error('base API 资源路径不合法')
  }
  return normalized
}

const requireId = (id: AdminBaseId): string => {
  const value = String(id).trim()
  if (!value || (typeof id === 'number' && !Number.isFinite(id))) {
    throw new Error('base 记录 ID 不合法')
  }
  return encodeURIComponent(value)
}

const requireNoQueryToken = <T extends object>(params: T): T => {
  if (Object.keys(params).some(key => TOKEN_QUERY_KEY.test(key))) {
    throw new Error('Admin token 禁止进入 URL 查询参数')
  }
  return params
}

export const parseAdminBasePageData = <TRow>(
  response: ApiResponse<AdminPageData<TRow>>,
  resourceLabel: string
): AdminPageData<TRow> => {
  const data = response.data
  if (!data || !Array.isArray(data.list) || typeof data.total !== 'number') {
    throw new AdminApiError(`${resourceLabel}分页响应格式错误`, response.code)
  }
  return data
}

export const parseAdminBaseDetail = <TRow extends AdminBaseRecord>(
  response: ApiResponse<TRow>,
  resourceLabel: string
): TRow => {
  const data = response.data
  if (!data || typeof data !== 'object' || data.id === null || data.id === undefined) {
    throw new AdminApiError(`${resourceLabel}详情响应格式错误`, response.code)
  }
  return data
}

const getHeader = (response: AxiosResponse<Blob>, name: string): string => {
  const headers = response.headers as unknown as {
    get?: (key: string) => unknown
    [key: string]: unknown
  }
  const value = typeof headers.get === 'function'
    ? headers.get(name)
    : headers[name] ?? headers[name.toLowerCase()]
  return typeof value === 'string' ? value : ''
}

const safeFilename = (value: string, fallback: string): string => {
  const sanitized = value
    .replace(/[\u0000-\u001f\u007f]/g, '')
    .replace(/[\\/]/g, '_')
    .trim()
    .slice(0, 180)
  return sanitized || fallback
}

export const parseAdminDownloadFilename = (
  contentDisposition: string,
  fallback: string
): string => {
  const encoded = contentDisposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i)?.[1]
  if (encoded) {
    try {
      return safeFilename(decodeURIComponent(encoded.replace(/^"|"$/g, '')), fallback)
    } catch {
      return fallback
    }
  }

  const plain = contentDisposition.match(/filename\s*=\s*(?:"([^"]+)"|([^;]+))/i)
  return safeFilename(plain?.[1] || plain?.[2] || '', fallback)
}

export const parseAdminBlobResponse = async (
  response: AxiosResponse<Blob>,
  fallbackFilename: string
): Promise<AdminBlobDownload> => {
  if (!(response.data instanceof Blob)) {
    throw new AdminApiError('Admin 导出响应格式错误', 'INVALID_BLOB')
  }

  const contentType = getHeader(response, 'content-type').toLowerCase()
  if (contentType.includes('application/json')) {
    try {
      const payload = JSON.parse(await response.data.text()) as ApiResponse
      throw new AdminApiError(payload.msg || 'Admin 导出失败', payload.code)
    } catch (error) {
      if (error instanceof AdminApiError) throw error
      throw new AdminApiError('Admin 导出错误响应格式错误', 'INVALID_BLOB_ERROR')
    }
  }

  return {
    blob: response.data,
    filename: parseAdminDownloadFilename(
      getHeader(response, 'content-disposition'),
      fallbackFilename
    )
  }
}

export const createAdminBaseCrudApi = <
  TRow extends AdminBaseRecord,
  TFilters extends AdminQueryFilters,
  TCreate extends object,
  TUpdate extends object
>(
  resourcePath: string,
  resourceLabel: string,
  exportFilename = `${resourceLabel}.xlsx`
): AdminBaseCrudApi<TRow, TFilters, TCreate, TUpdate> => {
  const path = requireResourcePath(resourcePath)
  const label = resourceLabel.trim() || 'base 记录'

  return {
    async page(query: AdminPageQuery<TFilters>): Promise<AdminPageData<TRow>> {
      const response = await adminClient.get<ApiResponse<AdminPageData<TRow>>>(`${path}/page`, {
        params: requireNoQueryToken(query)
      })
      return parseAdminBasePageData(response.data, label)
    },
    async detail(id: AdminBaseId): Promise<TRow> {
      const response = await adminClient.get<ApiResponse<TRow>>(`${path}/${requireId(id)}`)
      return parseAdminBaseDetail(response.data, label)
    },
    async create(payload: TCreate): Promise<void> {
      await adminClient.post<ApiResponse>(path, payload)
    },
    async update(payload: TUpdate): Promise<void> {
      await adminClient.put<ApiResponse>(path, payload)
    },
    async remove(ids: readonly AdminBaseId[]): Promise<void> {
      const targets = [...new Set(ids)].filter(id => String(id).trim())
      if (!targets.length) throw new Error('base 删除目标不能为空')
      await adminClient.delete<ApiResponse>(path, { data: targets })
    },
    async exportTable(filters: TFilters): Promise<AdminBlobDownload> {
      const response = await adminClient.get<Blob>(`${path}/export`, {
        params: requireNoQueryToken(filters),
        responseType: 'blob'
      })
      return parseAdminBlobResponse(response, exportFilename)
    }
  }
}
